<script setup>
    import { useI18n } from 'vue-i18n';
    import { ref } from 'vue';
    import DccsForm from '../DccsForm/DccsForm.vue';
    import DccsInputField from '../DccsForm/components/DccsInputField/DccsInputField.vue';
    import DccsEmailInputField from '../DccsForm/components/DccsEmailInputField/DccsEmailInputField.vue';
    import DccsCheckbox from '../DccsForm/components/DccsCheckbox/DccsCheckbox.vue';
    import DccsSelectField from '../DccsForm/components/DccsSelectField/DccsSelectField.vue';
    import DccsHiddenField from '../DccsForm/components/DccsHiddenField/DccsHiddenField.vue';

    const { t } = useI18n();

    const props = defineProps({
        action: {
            type: String,
            default: '/o/website-forms/newsletter/subscribe',
        },
        formAction: {
            type: String,
            default: 'https://cloud.opticalsolutions.ams-osram.com/confirmationamsosram',
        },
        firstNamePlaceholder: {
            type: String,
            default: '',
        },
        lastNamePlaceholder: {
            type: String,
            default: '',
        },
        emailPlaceholder: {
            type: String,
            default: '',
        },
        phonePlaceholder: {
            type: String,
            default: '',
        },
        companyPlaceholder: {
            type: String,
            default: '',
        },
        positionPlaceholder: {
            type: String,
            default: '',
        },
        levelPlaceholder: {
            type: String,
            default: '',
        },
    });

    const form = ref({
        FirstName: '',
        LastName: '',
        Email: '',
        Company: '',
        Phone: '',
        MobilePhone: '',
        position: '',
        level: '',
        newsletteroptin: false,
    });

    const positionOptions = [
        { key: '', value: t('newsletter.form.placeholder.position', 'Position') },
        { key: 'Engineering', value: t('newsletter.form.position.value.engineering', 'Engineering') },
        { key: 'Purchasing', value: t('newsletter.form.position.value.purchasing', 'Purchasing') },
        { key: 'Sales', value: t('newsletter.form.position.value.sales', 'Sales') },
        { key: 'Finance', value: t('newsletter.form.position.value.finance', 'Finance') },
    ];

    const levelOptions = [
        { key: '', value: t('newsletter.form.placeholder.level', 'Level') },
        { key: 'Junior', value: t('newsletter.form.level.value.junior', 'Junior') },
        { key: 'Senior', value: t('newsletter.form.level.value.senior', 'Senior') },
        { key: 'Manager', value: t('newsletter.form.level.value.manager', 'Manager') },
        { key: 'Senior Manager', value: t('newsletter.form.level.value.senior.manager', 'Senior Manager') },
        { key: 'Director', value: t('newsletter.form.level.value.director', 'Director') },
        { key: 'Vice President', value: t('newsletter.form.level.value.vice.president', 'Vice President') },
        { key: 'Board', value: t('newsletter.form.level.value.board', 'Board') },
    ];
</script>

<template>
    <DccsForm
        :action="props.action"
        :formAction="props.formAction"
        :button-label="t('newsletter.form.button.submit', 'Submit')"
        :submit-success-msg="
            t(
                'newsletterForm_submitSuccessMsg',
                'Thank you! Your newsletter subscription has been successfully submitted.'
            )
        "
        :submit-error-msg="
            t(
                'newsletterForm_submitErrorMsg',
                'Error! Your newsletter subscription could not be submitted. Please try again.'
            )
        "
        form-title="NewsletterForm"
        tracking-title="Newsletter Subscription"
        class=""
    >
        <DccsHiddenField name="formAction" :value="props.formAction" />
        <DccsInputField
            type="text"
            name="FirstName"
            :label="t('newsletter.form.label.first.name', 'First Name*')"
            :placeholder="props.firstNamePlaceholder || t('newsletter.form.placeholder.first.name', 'First name*')"
            v-model="form.FirstName"
            required
        />
        <DccsInputField
            type="text"
            name="LastName"
            :label="t('newsletter.form.label.last.name', 'Last Name*')"
            :placeholder="props.lastNamePlaceholder || t('newsletter.form.placeholder.last.name', 'Last name*')"
            v-model="form.LastName"
            required
        />
        <DccsEmailInputField
            field-name="Email"
            :field-label="t('newsletter.form.label.email', 'Email*')"
            :field-placeholder="props.emailPlaceholder || t('newsletter.form.placeholder.email', 'Email*')"
            v-model="form.Email"
            required
        />
        <DccsInputField
            type="text"
            name="Company"
            :label="t('newsletter.form.label.company', 'Company*')"
            :placeholder="props.companyPlaceholder || t('newsletter.form.placeholder.company', 'Company*')"
            v-model="form.Company"
            required
        />
        <DccsInputField
            type="tel"
            name="Phone"
            :label="t('newsletter.form.label.phone', 'Phone')"
            :placeholder="props.phonePlaceholder || t('newsletter.form.placeholder.phone', 'Phone')"
            v-model="form.Phone"
        />
        <DccsInputField
            type="tel"
            name="MobilePhone"
            :label="t('newsletter.form.label.mobile', 'Mobile')"
            :placeholder="t('newsletter.form.placeholder.mobile', 'Mobile')"
            v-model="form.MobilePhone"
        />
        <DccsSelectField
            name="position"
            :label="t('newsletter.form.label.position', 'Position')"
            :options="positionOptions"
            v-model="form.position"
            :required="false"
        />
        <DccsSelectField
            name="level"
            :label="t('newsletter.form.label.level', 'Level')"
            :options="levelOptions"
            v-model="form.level"
            :required="false"
        />
        <div class="has-checkbox">
            <DccsCheckbox name="newsletteroptin" v-model="form.newsletteroptin" required>
                <template #default>
                    <span
                        class="control-label"
                        v-html="
                            t(
                                'newsletter.form.checkbox.label',
                                'I agree that ams OSRAM as well as its distribution- and retail partners uses my email address for the purposes of personalised and generalised updates related to the products of ams OSRAM according to the ams OSRAM <a target=_blank href=/privacy-policy>privacy policy.</a>'
                            )
                        "
                    ></span>
                </template>
            </DccsCheckbox>
        </div>
    </DccsForm>
</template>

<style scoped>
    .dccs-field {
        margin-bottom: 1rem;
    }
    .dccs-label {
        font-weight: bold;
    }
    .dccs-control {
        margin-top: 0.25rem;
    }
    .dccs-select select {
        width: 100%;
    }
    .has-checkbox {
        margin-top: 1rem;
    }
</style>
